<template>
  <div :class="chickletClasses" :title="title" class="chicklet">
    <span
      v-if="maxWidth"
      :style="{ maxWidth: maxWidth }"
      class="chicklet__content"
    >
      <slot />
    </span>
    <span v-else class="chicklet__content">
      <slot />
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { availableSizeOptions } from '@/types/button-style-options'

const availableTypeOptions = ['flat', 'transparent', 'rounded', 'outline']

const availableColorOptions = [
  'grey',
  'algae-green',
  'bluey-purple',
  'bluish-grey',
  'dark-teal',
  'darkish-pink',
  'dodger-blue',
  'duck-egg-blue',
  'light-orange',
  'light-peach',
  'orange',
  'reddish-orange',
  'strawberry',
  'very-light-purple',
  'viridian',
]

export default defineComponent({
  name: 'Chicklet',

  props: {
    type: {
      type: String,
      default: 'flat',
      validator: (value: string) => {
        return availableTypeOptions.includes(value)
      },
    },

    color: {
      type: String,
      default: 'grey',
      validator: (value: string) => {
        return availableColorOptions.includes(value)
      },
    },

    size: {
      type: String,
      default: 'medium',
      validator: (value: string) => {
        return availableSizeOptions.includes(value)
      },
    },

    isUppercase: {
      type: Boolean,
      default: false,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    maxWidth: {
      type: String,
      default: null,
    },

    title: {
      type: String,
      default: null,
    },
  },

  computed: {
    colorClass(): string {
      const colorType = ['flat', 'rounded'].includes(this.type)
        ? 'flat'
        : this.type
      return `bg-${this.color}-${colorType}`
    },

    chickletClasses(): any {
      const baseSelector = 'chicklet'

      return {
        [this.colorClass]: true,
        [`${baseSelector}--${this.type}`]: true,
        [`${baseSelector}--${this.size}`]: true,
        [`${this.colorClass}--not-hover`]: true,
        [`${baseSelector}--uppercase`]: this.isUppercase,
        [`${baseSelector}--disabled`]: this.isDisabled,
      }
    },
  },
})
</script>

<style lang="scss">
.chicklet {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  &:focus {
    outline: none;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    white-space: nowrap;
    line-height: 1;
    max-width: 100%;

    > span {
      @include single-line-ellipsis;
    }
  }

  /* Uppercase */
  &--uppercase {
    text-transform: uppercase;
  }

  /* Disabled */
  &--disabled {
    user-select: none;
    opacity: 0.6;
  }

  /* Types */
  &--flat,
  &--transparent,
  &--outline {
    border-radius: 6px;
  }

  /* Sizes */
  &--tiny {
    height: 20px;
    padding: 0 8px;
    font-size: 10px;
    letter-spacing: 0.4px;

    &.chicklet--rounded {
      border-radius: 10px;
    }
  }

  &--small {
    height: 21px;
    padding: 2px 8px;
    font-size: 11px;
    letter-spacing: 0.4px;

    &.chicklet--rounded {
      border-radius: 10px;
    }
  }

  &--medium {
    height: 28px;
    padding: 0 10px;
    font-size: 11px;
    letter-spacing: 0.4px;

    &.chicklet--rounded {
      border-radius: 16px;
    }
  }

  &--large {
    height: 40px;
    padding: 0 20px;
    font-size: 14px;
    letter-spacing: 0.4px;

    &.chicklet--rounded {
      border-radius: 20px;
    }
  }
}
</style>
